import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  PhoneAuthProvider,
  RecaptchaVerifier,
  updatePhoneNumber,
  linkWithCredential,
  signInWithCredential
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { trackUserLogin } from '../services/analyticsService';

// Register new user
export const registerUser = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

// User login
export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

// User logout
export const logoutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    throw error;
  }
};

// Track authentication state changes
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Google login
export const loginWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();

    // Request standard scopes for basic profile access
    provider.addScope('profile');
    provider.addScope('email');
    
    // Set custom parameters for Google login
    provider.setCustomParameters({
      'prompt': 'select_account',        // Allow user to select account each time
      'auth_type': 'reauthenticate',     // Force re-authentication
      'include_granted_scopes': 'true'   // Include previously granted scopes
    });
    
    // Perform the sign-in with popup
    const result = await signInWithPopup(auth, provider);
    
    // Track successful login
    trackUserLogin('google');
    
    // Log success in development environments only
    if (process.env.NODE_ENV !== 'production') {
      console.log('Google login successful');
    }
    
    return result.user;
  } catch (error) {
    // Re-throw for handling by the caller
    throw error;
  }
};

// Safely clear any existing reCAPTCHA instance
const clearExistingRecaptcha = () => {
  if (window.recaptchaVerifier) {
    try {
      window.recaptchaVerifier.clear();
    } catch (e) {
      console.error('Error clearing existing reCAPTCHA:', e);
    }
    window.recaptchaVerifier = null;
  }
};

// Ensure the container element is ready for reCAPTCHA
const prepareRecaptchaContainer = (containerId) => {
  // Wait for DOM to be fully loaded
  if (document.readyState !== 'complete') {
    console.warn('Document not fully loaded yet, waiting...');
  }

  // Get or create the container element
  let container = document.getElementById(containerId);
  
  if (!container) {
    console.error(`reCAPTCHA container element with ID '${containerId}' not found`);
    throw new Error(`reCAPTCHA container not found: ${containerId}`);
  }
  
  // Clear the container content
  container.innerHTML = '';
  
  // Add specific styling to ensure the container is visible and properly sized
  container.style.display = 'flex';
  container.style.justifyContent = 'center';
  container.style.alignItems = 'center';
  container.style.minHeight = '80px';
  
  return container;
};

// Initialize reCAPTCHA verifier for phone authentication
export const initializeRecaptcha = (containerId) => {
  try {
    // Clear any existing reCAPTCHA instances
    clearExistingRecaptcha();
    
    // Prepare the container
    const container = prepareRecaptchaContainer(containerId);
    
    console.log(`Creating new RecaptchaVerifier in container '${containerId}'`);
    
    // Use invisible size for better mobile experience
    window.recaptchaVerifier = new RecaptchaVerifier(auth, container, {
      size: 'normal',
      callback: () => {
        // reCAPTCHA solved successfully
        console.log('reCAPTCHA verified successfully');
      },
      'expired-callback': () => {
        // Reset reCAPTCHA when expired
        console.log('reCAPTCHA expired');
        clearExistingRecaptcha();
      },
      'error-callback': (error) => {
        // Handle reCAPTCHA errors
        console.error('reCAPTCHA error:', error);
      }
    });
    
    return window.recaptchaVerifier;
  } catch (error) {
    console.error('Error initializing reCAPTCHA:', error);
    throw error;
  }
};

// Send verification code to phone number
export const sendVerificationCode = async (phoneNumber, containerId) => {
  console.log(`Sending verification code to phone number: ${phoneNumber}`);
  
  try {
    // Ensure the container exists and is ready before initialization
    const container = prepareRecaptchaContainer(containerId);
    
    if (!container) {
      throw new Error('Failed to find or prepare reCAPTCHA container');
    }
    
    // Initialize reCAPTCHA with proper error handling
    const recaptchaVerifier = initializeRecaptcha(containerId);
    
    if (!recaptchaVerifier) {
      console.error('Failed to initialize reCAPTCHA verifier');
      throw new Error('Failed to initialize reCAPTCHA. Please refresh the page and try again.');
    }
    
    // Validate phone number format before sending
    if (!phoneNumber || !/^\+?[0-9]{10,15}$/.test(phoneNumber.replace(/[\s-]/g, ''))) {
      console.error('Invalid phone number format:', phoneNumber);
      const error = new Error('Invalid phone number format');
      error.code = 'auth/invalid-phone-number';
      throw error;
    }
    
    // Log the phone number before sending
    console.log(`Sending verification to: ${phoneNumber} using reCAPTCHA verifier`);
    
    // Add timeout for verifyPhoneNumber operation
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Phone verification request timed out')), 60000);
    });
    
    // Create the phone provider
    const provider = new PhoneAuthProvider(auth);
    
    // Race between the verification request and timeout
    console.log('Starting verification process...');
    const verificationId = await Promise.race([
      provider.verifyPhoneNumber(phoneNumber, recaptchaVerifier),
      timeoutPromise
    ]);
    
    console.log('Verification code sent successfully, received verificationId');
    return verificationId;
  } catch (error) {
    console.error('Error sending verification code:', error);
    
    // Reset reCAPTCHA if there's an error
    clearExistingRecaptcha();
    
    // Clean up the reCAPTCHA container
    const container = document.getElementById(containerId);
    if (container) {
      container.innerHTML = '';
    }
    
    // Transform timeout error to more user-friendly message
    if (error.message && error.message.includes('timed out')) {
      console.error('Verification request timed out');
      const timeoutError = new Error('בקשת האימות נכשלה עקב זמן תגובה ארוך. נא לנסות שוב.');
      timeoutError.code = 'auth/timeout';
      throw timeoutError;
    }
    
    throw error;
  }
};

// Verify phone number with the verification code
export const verifyPhoneNumber = async (verificationId, verificationCode) => {
  try {
    const credential = PhoneAuthProvider.credential(
      verificationId, 
      verificationCode
    );
    
    if (auth.currentUser) {
      // Link phone number with existing account
      await linkWithCredential(auth.currentUser, credential);
      return auth.currentUser;
    } else {
      throw new Error("No user is currently signed in");
    }
  } catch (error) {
    throw error;
  }
};

// Update user's phone number
export const updateUserPhoneNumber = async (verificationId, verificationCode) => {
  try {
    const phoneAuthCredential = PhoneAuthProvider.credential(
      verificationId, 
      verificationCode
    );
    
    await updatePhoneNumber(auth.currentUser, phoneAuthCredential);
    return auth.currentUser;
  } catch (error) {
    throw error;
  }
};

// Login with phone number (directly without linking to existing account)
export const loginWithPhoneNumber = async (verificationId, verificationCode) => {
  try {
    const credential = PhoneAuthProvider.credential(
      verificationId, 
      verificationCode
    );
    
    const userCredential = await signInWithCredential(auth, credential);
    return userCredential.user;
  } catch (error) {
    console.error('Error signing in with phone:', error);
    throw error;
  }
}; 